import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Settings, Users, ArrowLeft, UserPlus, CreditCard, UserCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth, Card, CreditBalance } from '@aaas/shared';
import { CreditPackagesGrid } from '../components/payment/CreditPackagesGrid';
import { TransactionHistory } from '../components/payment/TransactionHistory';
import { MembersOnly } from '../components/workspace/MembersOnly';
import { InvitationGenerator } from '../components/workspace/InvitationGenerator';
import { AppSeatsOverview } from '../components/workspace/AppSeatsOverview';
import { useWorkspaceCredits } from '../hooks/useWorkspaceCredits';
import { usePaymentPackages } from '../hooks/usePaymentPackages';
import { useTransactions } from '../hooks/useTransactions';
import { useCheckout } from '../hooks/useCheckout';

type SettingsTab = 'general' | 'members' | 'invitations' | 'billing' | 'assign-seats';

interface SidebarItem {
  id: SettingsTab;
  label: string;
  icon: typeof Settings;
  description: string;
}

export function WorkspaceSettingsPage() {
  const { currentWorkspace, userProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['portal', 'shared']);
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');

  // Pagination state for transactions
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  // React Query hooks
  const {
    data: creditsData,
    isLoading: creditsLoading,
    error: creditsError,
    refetch: refetchCredits,
  } = useWorkspaceCredits({
    enableAutoRefresh: activeTab === 'billing', // Auto-refresh only on Billing tab
  });

  const { data: packages, isLoading: packagesLoading, error: packagesError } = usePaymentPackages();

  const {
    data: transactions,
    isLoading: transactionsLoading,
    error: transactionsError,
  } = useTransactions({
    page: currentPage,
    page_size: PAGE_SIZE,
    enabled: activeTab === 'billing', // Only fetch when on Billing tab
    enableAutoRefresh: activeTab === 'billing', // Auto-refresh when on Billing tab
  });

  const {
    mutate: createCheckout,
    isPending: checkoutLoading,
    error: checkoutMutationError,
  } = useCheckout();

  // Handle tab change with URL update
  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    navigate(`/workspace/settings?tab=${tab}`, { replace: true });
  };

  // Set initial tab from URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab') as SettingsTab;
    const validTabs: SettingsTab[] = ['general', 'billing', 'members'];

    // Invitations and assign-seats only for workspace owners
    if (currentWorkspace?.role === 'owner') {
      validTabs.push('invitations', 'assign-seats');
    }

    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    } else if (tabParam && !validTabs.includes(tabParam)) {
      // If trying to access invalid tab, redirect to general
      setActiveTab('general');
    } else if (!tabParam) {
      // If no tab param, set default and update URL
      navigate('/workspace/settings?tab=general', { replace: true });
    }
  }, [searchParams, currentWorkspace?.role, navigate]);

  // Handle package variant selection
  const handleSelectVariant = (variantId: number) => {
    if (!currentWorkspace) return;
    createCheckout({
      workspaceId: currentWorkspace.id,
      variantId,
      userProfile: userProfile || undefined,
    });
  };

  // Check if user can manage billing (owner only)
  const canManageBilling = currentWorkspace?.role === 'owner';

  // Calculate if more transactions can be loaded
  const canLoadMore = transactions && transactions.transactions.length < transactions.total_count;

  // Error message from mutation
  const checkoutError = checkoutMutationError?.message || null;

  if (!currentWorkspace) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">{t('workspaceSettings.noWorkspace')}</h1>
          <p className="text-gray-600">{t('workspaceSettings.selectWorkspace')}</p>
        </div>
      </div>
    );
  }

  const sidebarItems: SidebarItem[] = [
    {
      id: 'general' as SettingsTab,
      label: t('workspaceSettings.tabs.general'),
      icon: Settings,
      description: t('workspaceSettings.tabs.generalDesc'),
    },
    {
      id: 'billing' as SettingsTab,
      label: t('workspaceSettings.tabs.billing'),
      icon: CreditCard,
      description: t('workspaceSettings.tabs.billingDesc'),
    },
    {
      id: 'members' as SettingsTab,
      label: t('workspaceSettings.tabs.members'),
      icon: Users,
      description: t('workspaceSettings.tabs.membersDesc'),
    },
    // Invitations and Assign Seats for workspace owners only
    ...(currentWorkspace.role === 'owner'
      ? [
          {
            id: 'invitations' as SettingsTab,
            label: t('workspaceSettings.tabs.invitations'),
            icon: UserPlus,
            description: t('workspaceSettings.tabs.invitationsDesc'),
          },
          {
            id: 'assign-seats' as SettingsTab,
            label: t('workspaceSettings.tabs.seats'),
            icon: UserCog,
            description: t('workspaceSettings.tabs.seatsDesc'),
          },
        ]
      : []),
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('workspaceSettings.general.heading')}</h2>
              <p className="text-gray-600">{t('workspaceSettings.general.subheading')}</p>
            </div>

            <Card className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('workspaceSettings.general.workspaceName')}
                  </label>
                  <input
                    type="text"
                    value={currentWorkspace.name}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('workspaceSettings.general.workspaceCode')}
                  </label>
                  <input
                    type="text"
                    value={currentWorkspace.code}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('workspaceSettings.general.codeHint')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('workspaceSettings.general.workspaceType')}
                  </label>
                  <input
                    type="text"
                    value={t(`shared:workspaceSwitcher.type.${currentWorkspace.type}`)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('workspaceSettings.general.yourRole')}
                  </label>
                  <input
                    type="text"
                    value={t(`shared:workspaceSwitcher.role.${currentWorkspace.role}`, {
                      defaultValue: currentWorkspace.role,
                    })}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
              </div>
            </Card>
          </div>
        );

      case 'billing':
        return (
          <div className="space-y-6">
            {/* Header */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('workspaceSettings.billing.heading')}</h2>
              <p className="text-gray-600">{t('workspaceSettings.billing.subheading')}</p>
            </div>

            {/* Current Balance Card */}
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('workspaceSettings.billing.currentBalance')}</h3>
                  <p className="text-sm text-gray-600">{t('workspaceSettings.billing.sharedNote')}</p>
                </div>
                <div className="text-right">
                  <CreditBalance
                    credits={creditsData?.credits ?? null}
                    loading={creditsLoading}
                    error={creditsError?.message ?? null}
                    onRefresh={refetchCredits}
                    size="lg"
                    showRefreshButton={true}
                    showIcon={true}
                  />
                </div>
              </div>
            </Card>

            {/* Purchase Credits Section (Owner/Admin only) */}
            {canManageBilling && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('workspaceSettings.billing.purchaseCredits')}</h3>
                {checkoutError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{checkoutError}</p>
                  </div>
                )}
                <CreditPackagesGrid
                  packages={packages || []}
                  loading={packagesLoading}
                  error={packagesError?.message ?? null}
                  onSelectVariant={handleSelectVariant}
                  processingVariantId={checkoutLoading ? packages?.[0]?.variants[0]?.id : null}
                />
              </Card>
            )}

            {/* Transaction History Section */}
            <Card className="p-6">
              <TransactionHistory
                transactions={transactions || null}
                loading={transactionsLoading}
                error={transactionsError?.message ?? null}
                onLoadMore={() => setCurrentPage((prev) => prev + 1)}
                canLoadMore={canLoadMore}
              />
            </Card>
          </div>
        );

      case 'members':
        return <MembersOnly />;

      case 'invitations':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('workspaceSettings.invitations.heading')}</h2>
              <p className="text-gray-600">{t('workspaceSettings.invitations.subheading')}</p>
            </div>
            <InvitationGenerator
              workspaceId={currentWorkspace.id}
              workspaceName={currentWorkspace.name}
              appUrl={import.meta.env.VITE_APP_URL}
            />
          </div>
        );

      case 'assign-seats':
        return <AppSeatsOverview />;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>{t('workspaceSettings.backToApps')}</span>
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <h1 className="text-xl font-semibold text-gray-900">
                {t('workspaceSettings.pageTitle', {
                  defaultValue: '{{name}} Settings',
                  name: currentWorkspace.name,
                })}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar */}
          <div className="col-span-12 lg:col-span-3">
            <nav className="space-y-1">
              {sidebarItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;

                return (
                  <button
                    key={item.id}
                    onClick={() => handleTabChange(item.id)}
                    className={`w-full flex items-start space-x-3 px-3 py-3 rounded-lg text-left transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 mt-0.5 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}
                    />
                    <div className="flex-1">
                      <div
                        className={`font-medium ${isActive ? 'text-blue-900' : 'text-gray-900'}`}
                      >
                        {item.label}
                      </div>
                      <div className={`text-sm ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                        {item.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="col-span-12 lg:col-span-9">
            <div className="min-h-[600px] w-full">{renderContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
